import { useEffect, useId, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTrampaDeFoco } from '../ganchos/useTrampaDeFoco'
import {
  api,
  Cliente,
  CuentaDeCatalogo,
  ErrorDeApi,
  GeneracionDePlantilla,
  PeriodicidadPlantilla,
  PlantillaDePoliza as PlantillaDePolizaModelo,
  TipoPoliza,
} from '../servicios/api'
import { telon } from '../ganchos/telon'
import { useAvisos } from '../componentes/Toast'

const dinero = (n: number) =>
  n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']

/**
 * Plantillas de póliza recurrentes (depreciación, rentas, provisiones) y su generación
 * programada por lote: el motor corre solo, pero cada mes deja una GENERACIÓN PENDIENTE de
 * revisión — nunca contabiliza a ciegas. Un contador aprueba (se registra la póliza real, con
 * la misma partida doble de siempre) o rechaza (no se registra nada). SRS: RF-405 · RN-01, RN-06.
 */
export function PlantillasDePoliza() {
  const { id: clienteId } = useParams<{ id: string }>()
  const [cliente, setCliente] = useState<Cliente>()
  const [plantillas, setPlantillas] = useState<PlantillaDePolizaModelo[]>()
  const [pendientes, setPendientes] = useState<GeneracionDePlantilla[]>()
  const [error, setError] = useState('')
  const avisar = useAvisos()
  const [creando, setCreando] = useState(false)
  const [corriendo, setCorriendo] = useState(false)
  const [rechazando, setRechazando] = useState<GeneracionDePlantilla | null>(null)
  const [resolviendo, setResolviendo] = useState<string | null>(null)

  async function recargar() {
    if (!clienteId) return
    try {
      const [datosCliente, listaPlantillas, listaPendientes] = await Promise.all([
        api.obtenerCliente(clienteId),
        api.listarPlantillas(clienteId),
        api.listarGeneracionesDePlantilla(clienteId),
      ])
      setCliente(datosCliente)
      setPlantillas(listaPlantillas)
      setPendientes(listaPendientes)
      setError('')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron cargar las plantillas.')
    }
  }

  useEffect(() => {
    void recargar()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId])

  if (error && !plantillas) return <p className="error" role="alert">{error}</p>
  if (!clienteId || !cliente || !plantillas || !pendientes) return <p>Cargando…</p>

  async function alternarEstado(plantilla: PlantillaDePolizaModelo) {
    try {
      if (plantilla.estado === 'Activa') {
        await api.desactivarPlantilla(clienteId!, plantilla.id)
        avisar(`Plantilla "${plantilla.concepto}" desactivada; no se elimina, solo deja de generar (RN-09).`)
      } else {
        await api.reactivarPlantilla(clienteId!, plantilla.id)
        avisar(`Plantilla "${plantilla.concepto}" reactivada.`)
      }
      await recargar()
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo cambiar el estado de la plantilla.')
    }
  }

  async function correrAhora() {
    setCorriendo(true)
    try {
      const { creadas } = await api.correrGeneracionDePlantillasAhora(clienteId!)
      avisar(
        creadas > 0
          ? `Se generaron ${creadas} ${creadas === 1 ? 'pendiente nueva' : 'pendientes nuevas'} para revisar.`
          : 'No hay pendientes nuevas: ya se generó lo que corresponde a este mes, o el periodo está cerrado.',
      )
      await recargar()
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo correr el motor de generación.')
    } finally {
      setCorriendo(false)
    }
  }

  async function aprobar(generacion: GeneracionDePlantilla) {
    setResolviendo(generacion.id)
    try {
      const poliza = await api.aprobarGeneracionDePlantilla(generacion.id)
      avisar(`Generación de ${MESES[generacion.mes - 1]} ${generacion.anio} aprobada: se registró la póliza ${poliza.folioVisible}.`)
      await recargar()
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo aprobar la generación.')
    } finally {
      setResolviendo(null)
    }
  }

  return (
    <>
      <p className="sutil"><Link to={`/clientes/${clienteId}`}>← {cliente.razonSocial}</Link></p>
      <header className="encabezado">
        <h1>Plantillas de póliza recurrentes</h1>
        <span>
          <button onClick={() => void correrAhora()} disabled={corriendo}>
            {corriendo ? 'Corriendo…' : 'Correr generación ahora'}
          </button>{' '}
          <button className="primario" onClick={() => setCreando(true)}>Nueva plantilla</button>
        </span>
      </header>

      <p className="sutil">
        Depreciación, rentas, provisiones... El motor genera cada mes una PENDIENTE por revisar; nunca
        contabiliza solo (RF-405, RN-01, RN-06).
      </p>

      {error && <p className="error" role="alert">{error}</p>}

      <h2>Generaciones pendientes de revisión</h2>
      {pendientes.length === 0 ? (
        <p className="vacio">Sin generaciones pendientes por ahora.</p>
      ) : (
        <div className="tabla-envoltura">
          <table className="tabla">
            <thead>
              <tr><th>Periodo</th><th>Plantilla</th><th>Creada</th><th></th></tr>
            </thead>
            <tbody>
              {pendientes.map((g) => (
                <tr key={g.id}>
                  <td>{MESES[g.mes - 1]} {g.anio}</td>
                  <td>{g.plantillaConcepto}</td>
                  <td className="sutil">{new Date(g.creadaEnUtc).toLocaleDateString('es-MX')}</td>
                  <td>
                    <button className="enlace" disabled={resolviendo === g.id} onClick={() => void aprobar(g)}>
                      {resolviendo === g.id ? 'Aprobando…' : 'Aprobar'}
                    </button>{' '}
                    <button className="enlace" disabled={resolviendo === g.id} onClick={() => setRechazando(g)}>
                      Rechazar
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2>Plantillas configuradas</h2>
      {plantillas.length === 0 ? (
        <p className="vacio">Sin plantillas configuradas.</p>
      ) : (
        <div className="tabla-envoltura">
          <table className="tabla">
            <thead>
              <tr>
                <th>Concepto</th><th>Tipo</th><th>Periodicidad</th>
                <th style={{ textAlign: 'right' }}>Cargos</th>
                <th style={{ textAlign: 'right' }}>Abonos</th>
                <th>Estado</th><th></th>
              </tr>
            </thead>
            <tbody>
              {plantillas.map((p) => (
                <tr key={p.id} style={{ opacity: p.estado === 'Desactivada' ? 0.6 : 1 }}>
                  <td>{p.concepto}</td>
                  <td>{p.tipo}</td>
                  <td>{p.periodicidad}</td>
                  <td className="mono" style={{ textAlign: 'right' }}>{dinero(p.totalCargos)}</td>
                  <td className="mono" style={{ textAlign: 'right' }}>{dinero(p.totalAbonos)}</td>
                  <td>
                    <span className={`insignia ${p.estado === 'Activa' ? 'vigente' : 'vencido'}`}>{p.estado}</span>
                  </td>
                  <td>
                    <button className="enlace" onClick={() => void alternarEstado(p)}>
                      {p.estado === 'Activa' ? 'Desactivar' : 'Reactivar'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="nota">
        Σ cargos = Σ abonos en cada plantilla (RN-01), igual que cualquier póliza. Una plantilla
        desactivada no genera nada nuevo, pero no se elimina (RN-09).
      </p>

      {creando && (
        <CapturaDePlantilla
          clienteId={clienteId}
          alCerrar={(mensaje) => {
            setCreando(false)
            if (mensaje) avisar(mensaje)
            void recargar()
          }}
        />
      )}

      {rechazando && (
        <RechazoDeGeneracion
          generacion={rechazando}
          alCerrar={(mensaje) => {
            setRechazando(null)
            if (mensaje) avisar(mensaje)
            void recargar()
          }}
        />
      )}
    </>
  )
}

/** Rechazo con motivo obligatorio: no se registra ninguna póliza (RF-405). */
function RechazoDeGeneracion({
  generacion,
  alCerrar,
}: {
  generacion: GeneracionDePlantilla
  alCerrar: (aviso?: string) => void
}) {
  const idTitulo = useId()
  const panelRef = useTrampaDeFoco<HTMLFormElement>(true, alCerrar)
  const [error, setError] = useState('')
  const [enviando, setEnviando] = useState(false)

  async function rechazar(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    const motivo = String(new FormData(evento.currentTarget).get('motivo'))
    setError('')
    setEnviando(true)
    try {
      await api.rechazarGeneracionDePlantilla(generacion.id, motivo)
      alCerrar(`Generación de ${MESES[generacion.mes - 1]} ${generacion.anio} rechazada: no se registró ninguna póliza.`)
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo rechazar la generación.')
      setEnviando(false)
    }
  }

  return (
    <div className="modal" {...telon(() => alCerrar())}>
      <form
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        className="panel"
        onClick={(e) => e.stopPropagation()}
        onSubmit={rechazar}
      >
        <h2 id={idTitulo}>Rechazar generación de {generacion.plantillaConcepto}</h2>
        <p className="sutil">
          {MESES[generacion.mes - 1]} {generacion.anio}. Al rechazar no se registra ninguna póliza (RF-405).
        </p>
        <label>
          Motivo del rechazo (obligatorio)
          <input name="motivo" required maxLength={500} placeholder="No aplica este mes" />
        </label>
        {error && <p className="error" role="alert">{error}</p>}
        <div className="acciones">
          <button type="button" onClick={() => alCerrar()}>Volver</button>
          <button type="submit" className="primario" disabled={enviando}>
            {enviando ? 'Rechazando…' : 'Rechazar generación'}
          </button>
        </div>
      </form>
    </div>
  )
}

interface RenglonDePlantillaForm {
  cuentaId: string
  cargo: string
  abono: string
  descripcion: string
}

const RENGLON_VACIO: RenglonDePlantillaForm = { cuentaId: '', cargo: '', abono: '', descripcion: '' }

/** Alta de plantilla con partida doble en vivo: el registro se bloquea mientras haya descuadre (RN-01). */
function CapturaDePlantilla({
  clienteId,
  alCerrar,
}: {
  clienteId: string
  alCerrar: (aviso?: string) => void
}) {
  const idTitulo = useId()
  const panelRef = useTrampaDeFoco<HTMLDivElement>(true, alCerrar)
  const [cuentas, setCuentas] = useState<CuentaDeCatalogo[]>([])
  const [tipo, setTipo] = useState<TipoPoliza>('Diario')
  const [periodicidad] = useState<PeriodicidadPlantilla>('Mensual')
  const [concepto, setConcepto] = useState('')
  const [renglones, setRenglones] = useState<RenglonDePlantillaForm[]>([{ ...RENGLON_VACIO }, { ...RENGLON_VACIO }])
  const [error, setError] = useState('')
  const [enviando, setEnviando] = useState(false)

  useEffect(() => {
    api.catalogoDeCuentas(clienteId)
      .then((todas) => setCuentas(todas.filter((c) => c.esDeRegistro && c.estado === 'Activa')))
      .catch(() => setCuentas([]))
  }, [clienteId])

  const totalCargos = renglones.reduce((s, r) => s + (Number(r.cargo) || 0), 0)
  const totalAbonos = renglones.reduce((s, r) => s + (Number(r.abono) || 0), 0)
  const diferencia = Math.round((totalCargos - totalAbonos) * 100) / 100
  const descuadrada = diferencia !== 0 || totalCargos === 0

  function actualizar(indice: number, cambio: Partial<RenglonDePlantillaForm>) {
    setRenglones((r) => r.map((renglon, i) => (i === indice ? { ...renglon, ...cambio } : renglon)))
  }

  async function registrar() {
    setError('')
    setEnviando(true)
    try {
      const plantilla = await api.registrarPlantilla(clienteId, {
        tipo,
        concepto,
        periodicidad,
        renglones: renglones
          .filter((r) => r.cuentaId && (Number(r.cargo) > 0 || Number(r.abono) > 0))
          .map((r) => ({
            cuentaId: r.cuentaId,
            cargo: Number(r.cargo) || 0,
            abono: Number(r.abono) || 0,
            descripcion: r.descripcion.trim() || undefined,
          })),
      })
      alCerrar(`Plantilla "${plantilla.concepto}" creada: cargos = abonos = ${dinero(plantilla.totalCargos)}.`)
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo registrar la plantilla.')
      setEnviando(false)
    }
  }

  return (
    <div className="modal" {...telon(() => alCerrar())}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        className="panel"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(640px, 94vw)' }}
      >
        <h2 id={idTitulo}>Nueva plantilla · generación mensual</h2>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
          <label>
            Tipo
            <select value={tipo} onChange={(e) => setTipo(e.target.value as TipoPoliza)}>
              <option>Diario</option><option>Ingreso</option><option>Egreso</option>
            </select>
          </label>
          <label>
            Periodicidad
            <select value={periodicidad} disabled>
              <option>Mensual</option>
            </select>
          </label>
        </div>

        <label>
          Concepto
          <input value={concepto} onChange={(e) => setConcepto(e.target.value)} required maxLength={500}
            placeholder="Depreciación mensual de equipo de cómputo" />
        </label>

        <table className="tabla">
          <thead>
            <tr>
              <th>Cuenta</th><th style={{ width: 110 }}>Debe</th><th style={{ width: 110 }}>Haber</th>
              <th>Descripción</th><th style={{ width: 30 }}></th>
            </tr>
          </thead>
          <tbody>
            {renglones.map((renglon, i) => (
              <tr key={i}>
                <td>
                  <select value={renglon.cuentaId} onChange={(e) => actualizar(i, { cuentaId: e.target.value })}
                    style={{ width: '100%', padding: '6px 8px', fontFamily: 'inherit', fontSize: 13 }}>
                    <option value="">— cuenta de registro —</option>
                    {cuentas.map((c) => (
                      <option key={c.id} value={c.id}>{c.numero} · {c.nombre}</option>
                    ))}
                  </select>
                </td>
                <td><input inputMode="decimal" placeholder="0.00" value={renglon.cargo}
                  onChange={(e) => actualizar(i, { cargo: e.target.value, abono: e.target.value ? '' : renglon.abono })}
                  style={{ width: '100%', padding: '6px 8px', textAlign: 'right' }} /></td>
                <td><input inputMode="decimal" placeholder="0.00" value={renglon.abono}
                  onChange={(e) => actualizar(i, { abono: e.target.value, cargo: e.target.value ? '' : renglon.cargo })}
                  style={{ width: '100%', padding: '6px 8px', textAlign: 'right' }} /></td>
                <td><input value={renglon.descripcion} onChange={(e) => actualizar(i, { descripcion: e.target.value })}
                  placeholder="Opcional" maxLength={300} style={{ width: '100%', padding: '6px 8px', fontSize: 12 }} /></td>
                <td>
                  {renglones.length > 2 && (
                    <button className="enlace" title="Eliminar renglón"
                      onClick={() => setRenglones((r) => r.filter((_, j) => j !== i))}>×</button>
                  )}
                </td>
              </tr>
            ))}
            <tr>
              <td style={{ fontWeight: 700 }}>Sumas · deben ser iguales (RN-01)</td>
              <td className="mono" style={{ textAlign: 'right', fontWeight: 700 }}>{dinero(totalCargos)}</td>
              <td className="mono" style={{ textAlign: 'right', fontWeight: 700 }}>{dinero(totalAbonos)}</td>
              <td></td>
              <td></td>
            </tr>
          </tbody>
        </table>

        <p>
          <button className="enlace" onClick={() => setRenglones((r) => [...r, { ...RENGLON_VACIO }])}>
            + Agregar renglón
          </button>
        </p>

        {diferencia !== 0 && (
          <p className="error" role="alert">
            <strong>Plantilla descuadrada: diferencia de ${dinero(Math.abs(diferencia))}.</strong>{' '}
            No se guardan plantillas descuadradas (RN-01).
          </p>
        )}

        {error && <p className="error" role="alert">{error}</p>}

        <div className="acciones">
          <button onClick={() => alCerrar()}>Descartar</button>
          <button className="primario" disabled={descuadrada || enviando || !concepto} onClick={() => void registrar()}>
            {enviando ? 'Registrando…' : 'Registrar plantilla'}
          </button>
        </div>
      </div>
    </div>
  )
}
